import { ref, computed, watch, unref, type Ref, type MaybeRef } from 'vue';
import { isDefaultDisableHeaderList, TableType } from '@/coreDomainsModule/table';
import { diContainer } from '@/coreContainer/container';

/**
 * ヘッダーの表示非表示を管理する
 * @param fullHeaderListSource - 全てのヘッダーキーのリスト
 * @param tableTypeSource - テーブルの種類
 * @param defaultHiddenHeaderList - 初期表示の際に非表示にするヘッダー
 */
export const useTableHeader = (
  fullHeaderListSource: MaybeRef<string[]>,
  tableTypeSource: MaybeRef<TableType>,
  defaultHiddenHeaderList?: string[]
) => {
  const fullHeaderList = computed(() => unref(fullHeaderListSource) ?? []);
  const tableType = computed(() => unref(tableTypeSource));
  const headerList: Ref<string[]> = ref([]);
  let fetchRequestId = 0;

  /**
   * ヘッダーリストを取得する
   */
  const _getDisplayHeader = async (): Promise<{ headerList: string[]; allHeaderList: string[] } | undefined> => {
    const getHeaderListResult = await diContainer.getTableHeaderPreferenceUseCase.Execute(tableType.value);
    if (getHeaderListResult.status.type !== 'success') {
      // ヘッダーの取得に失敗した場合のエラー判別
      if (getHeaderListResult.status.error.message === 'preferenceの取得に失敗しました') {
        // preferenceに存在しない場合
        console.error('個人設定の取得に失敗しました。サーバーからのデータが存在しない可能性があります。');
      } else {
        // 対象のテーブルタイプのヘッダー情報が存在しない場合
        // このケースでは、ヘッダーリストおよび非表示リストの登録を行う
        console.error('ヘッダーの取得に失敗しました。tableType:', tableType.value, 'Error:', getHeaderListResult.status.error);
        // デフォルト表示ヘッダーリストを設定 (非表示考慮)
        headerList.value = fullHeaderList.value.filter((header) =>
          defaultHiddenHeaderList ? !defaultHiddenHeaderList.includes(header) : isDefaultDisableHeaderList(header)
        );
        // ヘッダーリストをDBに保存
        await _saveTableHeaders();
        // 初期表示の非表示ヘッダー情報をDBに保存
        // NOTE: bulkUpdateDisplayHeaderを使用する場合、内部で_getDisplayHeaderが呼ばれ無限ループに入る可能性があるので、ここでは直接preference更新処理を呼ぶ
        await diContainer.bulkSaveTableHeaderHidePreferenceUseCase.Execute(
          tableType.value,
          fullHeaderList.value.filter((header) =>
            defaultHiddenHeaderList ? defaultHiddenHeaderList.includes(header) : isDefaultDisableHeaderList(header)
          ),
          true
        );
        console.log('初期表示のヘッダーリストを設定しました。');
      }
      return;
    } else {
      return getHeaderListResult.status.data;
    }
  };

  /**
   * ヘッダーリストをDBに保存する
   */
  const _saveTableHeaders = async () => {
    await diContainer.saveTableHeaderPreferenceUseCase.Execute(tableType.value, fullHeaderList.value);
  };

  /**
   * ヘッダーリストをDBから取得して同期させる
   */
  const _fetchTableHeaders = async () => {
    const requestId = ++fetchRequestId;
    const fetchingTableType = tableType.value;
    // ヘッダーリストを取得
    const getHeaderListResult = await _getDisplayHeader();
    if (requestId !== fetchRequestId || fetchingTableType !== tableType.value) return [];
    if (!getHeaderListResult) {
      console.error('ヘッダーリストの取得に失敗しました。');
      return [];
    }

    // 取得したヘッダーがPropsのヘッダー内容と異なる場合はヘッダーを保存する & 再度ヘッダー情報を取得してstateのヘッダーリストを更新する
    const allHeaderList = getHeaderListResult.allHeaderList;
    if (fullHeaderList.value.length !== allHeaderList.length || !fullHeaderList.value.every((v, i) => v === allHeaderList[i])) {
      await _saveTableHeaders();
      console.log('ヘッダーリストを保存しました。');
      // 再度ヘッダー情報を取得してstateのヘッダーリストを更新する
      const updatedHeaderListResult = await _getDisplayHeader();
      if (requestId !== fetchRequestId || fetchingTableType !== tableType.value) return [];
      if (!updatedHeaderListResult) {
        console.error('ヘッダーリストの更新に失敗しました。');
        return [];
      }

      // stateにヘッダーリストを保存
      headerList.value = updatedHeaderListResult.headerList;
      console.log('ヘッダーリストを更新しました。');
    } else {
      // stateにヘッダーリストを保存
      headerList.value = getHeaderListResult.headerList;
      console.log('ヘッダーリストを更新しました。');
    }

    if (!allHeaderList.length) {
      // 初回表示の際はデフォルト非表示のヘッダーリストをDBに登録する
      await bulkUpdateDisplayHeader(
        fullHeaderList.value.filter((header) =>
          defaultHiddenHeaderList ? defaultHiddenHeaderList.includes(header) : isDefaultDisableHeaderList(header)
        ),
        true
      );
      console.log('初期表示のヘッダーリストを設定しました。');
    }
  };

  /**
   * ヘッダーの表示/非表示情報をDBに保存する
   * @param header - 変更対象のヘッダー
   * @param isHide - 非表示にするかどうか
   */
  const updateDisplayHeader = async (header: string, isHide: boolean) => {
    await diContainer.saveTableHeaderHidePreferenceUseCase.Execute(tableType.value, header, isHide);
    await _fetchTableHeaders();
  };

  /**
   * ヘッダーの表示/非表示情報を複数まとめてDBに保存する
   * @param headers - 変更対象のヘッダーリスト
   * @param isHide - 非表示にするかどうか
   */
  const bulkUpdateDisplayHeader = async (headers: string[], isHide: boolean) => {
    await diContainer.bulkSaveTableHeaderHidePreferenceUseCase.Execute(tableType.value, headers, isHide);
    await _fetchTableHeaders();
  };

  // fullHeaderList に変更があった場合、ヘッダーリストを更新し、DBに保存する
  // deep: true で中身の変更にも反応、immediate: true で初回も実行
  watch(
    [fullHeaderList, tableType],
    async ([nextHeaders, nextTableType], [prevHeaders, prevTableType] = [undefined, undefined]) => {
      // 変化検出（順序含めて比較）
      const changed =
        nextTableType !== prevTableType ||
        !prevHeaders ||
        nextHeaders.length !== prevHeaders.length ||
        nextHeaders.some((v, i) => v !== prevHeaders[i]);

      if (changed) {
        await _fetchTableHeaders();
      }
    },
    { deep: true, immediate: true } // deep で中身の変更にも反応、初回も実行
  );
  return { headerList: headerList, updateDisplayHeader, bulkUpdateDisplayHeader };
};
